import { useCallback, useSyncExternalStore } from "react";

import { GROOMING_BACKLOG_SORTS, type GroomingBacklogItem, type GroomingBacklogSort } from "@/lib/api/grooming";

/**
 * The Backlog page's sort menu (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1406;
 * E292, UX-PM-12; E303, ER-1656): Newest (default), ADO order or Oldest,
 * remembered per person in this browser or app (localStorage) and shared with
 * the Session list, plus the Opened column's date words. ER-1656 supersedes
 * UX-PM-12's ADO-order default. A stored choice is still honoured.
 */

/** localStorage key of the remembered sort. */
export const BACKLOG_SORT_STORAGE_KEY = "spectree.grooming.backlogSort";

/** The sort used when nothing (or something invalid) is remembered: Newest (ER-1656, supersedes UX-PM-12). */
export const BACKLOG_SORT_DEFAULT: GroomingBacklogSort = "newest";

/** The sort control's options, in order: Newest · ADO order · Oldest (frame K4). */
export const BACKLOG_SORT_MENU: readonly GroomingBacklogSort[] = ["newest", "rank", "oldest"];

/** The sort control's labels (frame K4: Newest · ADO order · Oldest). */
export const BACKLOG_SORT_LABELS: Readonly<Record<GroomingBacklogSort, string>> = {
  rank: "ADO order",
  newest: "Newest",
  oldest: "Oldest",
};

/** The subtitle's words for the order: "Ranked as in ADO · top 30 of 42" (frame D1). */
export const BACKLOG_SORT_WORDS: Readonly<Record<GroomingBacklogSort, string>> = {
  rank: "Ranked as in ADO",
  newest: "Newest first",
  oldest: "Oldest first",
};

/** The word the stage tiles use for a top-N scope: "of the newest 30" / "of the oldest 30" / "of the top 30". */
export const BACKLOG_SORT_SCOPE_WORDS: Readonly<Record<GroomingBacklogSort, string>> = {
  newest: "newest",
  oldest: "oldest",
  rank: "top",
};

export function isBacklogSort(value: unknown): value is GroomingBacklogSort {
  return typeof value === "string" && (GROOMING_BACKLOG_SORTS as readonly string[]).includes(value);
}

/**
 * The choice for this visit when storage rejected the write (private mode,
 * quota). Null while storage is the source of truth.
 */
let unpersistedSort: GroomingBacklogSort | null = null;

/** The remembered sort, or Newest when none is stored, it is invalid, or storage is unavailable. */
export function readBacklogSort(): GroomingBacklogSort {
  if (unpersistedSort !== null) return unpersistedSort;
  try {
    const stored = window.localStorage.getItem(BACKLOG_SORT_STORAGE_KEY);
    return isBacklogSort(stored) ? stored : BACKLOG_SORT_DEFAULT;
  } catch {
    return BACKLOG_SORT_DEFAULT;
  }
}

/** Remember the sort; a storage failure only means it lasts for this visit, not later ones. */
export function writeBacklogSort(sort: GroomingBacklogSort): void {
  try {
    window.localStorage.setItem(BACKLOG_SORT_STORAGE_KEY, sort);
    unpersistedSort = null;
  } catch {
    unpersistedSort = sort;
  }
}

/** Same-window change signal; the native 'storage' event only fires in other windows. */
const SORT_CHANGE_EVENT = "spectree:backlog-sort-change";

function subscribeSort(onChange: () => void): () => void {
  // Another window wrote the key: its value wins over this visit's unsaved choice.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== BACKLOG_SORT_STORAGE_KEY) return;
    unpersistedSort = null;
    onChange();
  };
  window.addEventListener(SORT_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(SORT_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * The remembered sort as state, shared by every component and window: a write
 * from one (the Backlog or the Session list) updates the others.
 */
export function useBacklogSort(): [GroomingBacklogSort, (sort: GroomingBacklogSort) => void] {
  const sort = useSyncExternalStore(subscribeSort, readBacklogSort, () => BACKLOG_SORT_DEFAULT);
  const setSort = useCallback((next: GroomingBacklogSort) => {
    writeBacklogSort(next);
    window.dispatchEvent(new Event(SORT_CHANGE_EVENT));
  }, []);
  return [sort, setSort];
}

/**
 * Rows in the chosen order: newest or oldest by createdDate (stable, missing
 * or invalid dates last), or the input order for ADO order. Never mutates.
 */
export function sortRows<T extends { createdDate: string | null }>(
  rows: readonly T[],
  sort: GroomingBacklogSort
): T[] {
  if (sort === "rank") return [...rows];
  const keyed = rows.map((row, index) => {
    const time = row.createdDate ? new Date(row.createdDate).getTime() : Number.NaN;
    return { row, index, time };
  });
  keyed.sort((a, b) => {
    const aMissing = Number.isNaN(a.time);
    const bMissing = Number.isNaN(b.time);
    if (aMissing || bMissing) {
      if (aMissing && bMissing) return a.index - b.index;
      return aMissing ? 1 : -1;
    }
    if (a.time === b.time) return a.index - b.index;
    return sort === "newest" ? b.time - a.time : a.time - b.time;
  });
  return keyed.map((entry) => entry.row);
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
const FULL = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" });

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The Opened column's words, in local time: "Oct 2" for this year, "Mar 2025"
 * for an older (or later) year, and "" when the date is missing or invalid.
 */
export function createdShort(iso: string | null | undefined, now: Date = new Date()): string {
  const date = parse(iso);
  if (!date) return "";
  return date.getFullYear() === now.getFullYear() ? MONTH_DAY.format(date) : MONTH_YEAR.format(date);
}

/** The Opened column's tooltip: the full local date and time, or "" when missing. */
export function createdFull(iso: string | null | undefined): string {
  const date = parse(iso);
  return date ? FULL.format(date) : "";
}

/** "Chris Patel · Oct 22": who opened the item and when; the date is left out when missing. */
export function whoDate(item: Pick<GroomingBacklogItem, "createdBy" | "createdDate">): string {
  const name = item.createdBy?.displayName ?? "Author not known";
  const date = createdShort(item.createdDate);
  return date ? `${name} · ${date}` : name;
}
